import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Robot, GitBranch, CurrencyDollar, HardDrives, ArrowRight } from '@phosphor-icons/react';

const catalogData = [
  {
    id: 'ai',
    label: 'Artificial Intelligence',
    icon: <Robot size={18} weight="bold" />,
    image: 'https://static.prod-images.emergentagent.com/jobs/f0f5f9d2-54c2-4e20-9504-6a4092245c91/images/e3d53d946bfbf8e5ae3e056dde750f08bdd852fc5dc7500e8bc7670d57144cbe.png',
    headline: 'Agentic AI & Intelligent Automation',
    description: 'Deploy autonomous AI agents that learn, adapt, and execute complex workflows. Our AI-Powered DevSecOps integrates seamlessly into your CI/CD pipeline for proactive security and operational excellence.',
    services: [
      { name: 'Agentic AIs', detail: 'Self-directed AI agents for enterprise automation' },
      { name: 'AI-Powered DevSecOps', detail: 'Intelligent security scanning embedded in pipelines' },
      { name: 'ML Model Training', detail: 'Scalable infrastructure for model training at scale' },
      { name: 'Predictive Analytics', detail: 'Real-time AI-driven business intelligence' },
    ],
  },
  {
    id: 'devops',
    label: 'DevOps & QA',
    icon: <GitBranch size={18} weight="bold" />,
    image: 'https://static.prod-images.emergentagent.com/jobs/f0f5f9d2-54c2-4e20-9504-6a4092245c91/images/d3e27c5072af9dd4c449e45761cf05506d752a8a5f956e8460290d2b88579e48.png',
    headline: 'CI/CD Automation & QA Excellence',
    description: 'End-to-end DevOps automation with intelligent testing suites. From pipeline orchestration to comprehensive APM tracking, we deliver operational reliability at scale.',
    services: [
      { name: 'CI/CD Automation', detail: 'Zero-downtime deployment pipelines' },
      { name: 'QA Testing Suites', detail: 'Automated regression, performance, and security testing' },
      { name: 'App APM Tracking', detail: 'Full-stack application performance monitoring' },
      { name: 'GitOps Workflows', detail: 'Infrastructure-as-code with declarative operations' },
    ],
  },
  {
    id: 'finops',
    label: 'Cloud & FinOps',
    icon: <CurrencyDollar size={18} weight="bold" />,
    image: 'https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg',
    headline: 'Cost Optimization & Cloud Strategy',
    description: 'Maximize cloud ROI with granular cost analytics and automated optimization. Our FinOps platform provides real-time visibility into cloud spend across all providers.',
    services: [
      { name: 'Cost Optimization Tooling', detail: 'AI-driven recommendations to cut cloud waste' },
      { name: 'Next-Gen Instance Migration', detail: 'Seamless workload migration across generations' },
      { name: 'Multi-Cloud Governance', detail: 'Unified policies across AWS, Azure, GCP' },
      { name: 'Budget Alerting', detail: 'Proactive spend management and forecasting' },
    ],
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure & Compute',
    icon: <HardDrives size={18} weight="bold" />,
    image: 'https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg',
    headline: 'Enterprise IaaS & Compute',
    description: 'Scalable infrastructure services from bare-metal to serverless. Deploy globally with our dual-region architecture spanning the USA and India.',
    services: [
      { name: 'Bare-Metal Servers', detail: 'Dedicated high-performance compute instances' },
      { name: 'Container Orchestration', detail: 'Managed Kubernetes and Docker environments' },
      { name: 'Serverless Functions', detail: 'Event-driven compute with zero infrastructure overhead' },
      { name: 'VPC & Networking', detail: 'Isolated networks with enterprise-grade security' },
    ],
  },
];

export default function ServiceCatalog() {
  return (
    <section id="service-catalog" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="service-catalog-section">
      <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
        {/* Section header */}
        <div className="mb-12 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-3">
            Service Catalog
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#0B132B]"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Explore Our Solutions
          </h2>
        </div>

        <Tabs defaultValue="ai" className="w-full">
          <TabsList className="w-full flex flex-wrap bg-white border border-slate-200 p-1.5 h-auto gap-1 rounded-none" data-testid="service-catalog-tabs">
            {catalogData.map((cat) => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className="flex items-center gap-2 px-8 py-4 text-lg font-bold rounded-none data-[state=active]:bg-[#0052FF] data-[state=active]:text-white data-[state=active]:shadow-none text-slate-600 hover:text-[#0B132B] transition-colors duration-200"
                data-testid={`service-tab-${cat.id}`}
              >
                {cat.icon}
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {catalogData.map((cat) => (
            <TabsContent key={cat.id} value={cat.id} className="mt-0" data-testid={`service-content-${cat.id}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white border border-slate-200 border-t-0">
                {/* Left: Text */}
                <div className="p-8 lg:p-12 flex flex-col justify-center text-center">
                  <h3
                    className="text-xl sm:text-2xl tracking-tight font-semibold text-[#0B132B] mb-4"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {cat.headline}
                  </h3>
                  <p className="text-base leading-relaxed text-[#475569] mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {cat.description}
                  </p>
                  <div className="space-y-4 inline-block text-left">
                    {cat.services.map((svc) => (
                      <div key={svc.name} className="flex items-start gap-3 group cursor-pointer">
                        <ArrowRight size={16} weight="bold" className="text-[#0052FF] mt-1 flex-shrink-0 group-hover:translate-x-1 transition-transform duration-200" />
                        <div>
                          <span className="text-base font-semibold text-[#0B132B]">{svc.name}</span>
                          <span className="text-base text-[#475569] ml-2">{svc.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Right: Image */}
                <div className="relative overflow-hidden bg-slate-100">
                  <img
                    src={cat.image}
                    alt={cat.headline}
                    className="w-full h-full object-cover min-h-[300px] lg:min-h-[400px]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/30 to-transparent" />
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
